import { CheckIcon, ExternalLinkIcon, LocateFixedIcon, MapPinIcon, SearchIcon, TriangleAlertIcon, XIcon } from 'lucide-react';
import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { toast } from 'sonner';
import { SectionTitle } from '@/components/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FormError } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useJobContext } from '@/layouts/JobLayout';
import { api, errorMessage } from '@/lib/api';
import { tzAbbreviation } from '@/lib/format';
import type { GeocodeResponse, GeocodeResult } from '@/lib/types';
import { cn } from '@/lib/utils';
import { useUpdateJob } from './queries';

const COORDS_RE = /^\s*(-?\d{1,2}(?:\.\d+)?)\s*[,\s]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/;
const PRECISION: Record<string, { label: string; hint: string }> = {
  rooftop: { label: 'Exact building', hint: 'A specific address or building.' },
  street: { label: 'Street', hint: 'Somewhere along this street; add the house number for a rooftop match.' },
  area: { label: 'Neighbourhood', hint: 'The middle of an area, not a specific site.' },
  city: { label: 'City', hint: 'The middle of the city; add a street for a precise site.' },
  region: { label: 'Region', hint: 'Far too coarse for weather and travel; add a street and city.' },
  unknown: { label: 'Approximate', hint: 'Check it on the map before saving.' },
};

function providerLabel(name: string | null | undefined): string {
  if (name === 'geocode-maps-co') return 'geocode.maps.co';
  if (name === 'nominatim') return 'OpenStreetMap Nominatim';
  if (name === 'azure-maps') return 'Azure Maps';
  if (name === 'mock') return 'Simulated lookup (development)';
  return name ?? 'address service';
}

/** "Kia Center, 400 West Church Street, Orlando, Florida 32801": short enough for a job address. */
function compactAddress(r: GeocodeResult): string {
  const cityLine = [r.locality, [r.region, r.postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const parts = [r.name && r.name !== r.street ? r.name : null, r.street, cityLine].filter(Boolean);
  return parts.length ? parts.join(', ') : r.formattedAddress;
}

function mapLinks(lat: number, lon: number) {
  return {
    google: `https://www.google.com/maps/search/?api=1&query=${lat.toFixed(6)},${lon.toFixed(6)}`,
    osm: `https://www.openstreetmap.org/?mlat=${lat.toFixed(6)}&mlon=${lon.toFixed(6)}#map=17/${lat.toFixed(5)}/${lon.toFixed(5)}`,
  };
}

/**
 * Job site location: type an address (or paste coordinates), pick the right candidate, check it on a map, save.
 * The chosen candidate's coordinates and timezone are saved with the address text as typed; "Use this address"
 * swaps the text for the service's tidy version.
 */
export function LocationSection() {
  const { job, permissions } = useJobContext();
  const update = useUpdateJob(job.id);
  const canEdit = permissions.editLocation;
  const [address, setAddress] = useState(job.address ?? '');
  const [timezone, setTimezone] = useState(job.timezone ?? '');
  const [response, setResponse] = useState<GeocodeResponse | null>(null);
  const [chosen, setChosen] = useState<GeocodeResult | null>(null);
  const [searchedFor, setSearchedFor] = useState('');
  const [looking, setLooking] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setAddress(job.address ?? '');
    setTimezone(job.timezone ?? '');
  }, [job.address, job.timezone]);

  const zones = useMemo(() => {
    try {
      return (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
    } catch {
      return [];
    }
  }, []);

  const hasCoords = job.latitude !== null && job.longitude !== null;
  const dirty = address !== (job.address ?? '') || timezone !== (job.timezone ?? '') || !!chosen;
  const coordsTyped = COORDS_RE.exec(address);

  const find = async () => {
    const q = address.trim();
    if (!q) return;
    setErr(null);
    setLooking(true);
    setChosen(null);
    let searched = q;
    try {
      const m = COORDS_RE.exec(q);
      if (m) {
        // Pasted coordinates: name the place and pin exactly there.
        const lat = Number(m[1]);
        const lon = Number(m[2]);
        if (Math.abs(lat) > 90 || Math.abs(lon) > 180) throw new Error('Latitude runs -90 to 90 and longitude -180 to 180.');
        const r = await api.automation.reverseGeocode(job.id, lat, lon);
        const pin: GeocodeResult = r.result ? { ...r.result, latitude: lat, longitude: lon, precision: 'rooftop' } : { provider: 'coordinates', formattedAddress: `${lat.toFixed(5)}, ${lon.toFixed(5)}`, latitude: lat, longitude: lon, timezone: null, precision: 'rooftop' };
        setResponse({ result: pin, results: [pin], provider: r.provider });
        const replacement = r.result ? compactAddress(r.result) : undefined;
        choose(pin, replacement);
        searched = replacement ?? q;
      } else {
        const r = await api.automation.geocode(job.id, q, 5);
        setResponse(r);
        if (r.results.length === 1) choose(r.results[0]!);
      }
      setSearchedFor(searched);
    } catch (e) {
      setErr(errorMessage(e));
      setResponse(null);
    } finally {
      setLooking(false);
    }
  };

  const choose = (r: GeocodeResult, replaceAddress?: string) => {
    setChosen(r);
    if (replaceAddress) setAddress(replaceAddress);
    if (r.timezone && (!timezone || timezone === (job.timezone ?? ''))) setTimezone(r.timezone);
  };

  const save = async () => {
    setErr(null);
    try {
      const text = address.trim();
      if (!text) {
        await update.mutateAsync({ address: null, latitude: null, longitude: null, timezone: timezone || null });
        toast.success('Job site location cleared');
      } else if (chosen) {
        await update.mutateAsync({ address: text, latitude: chosen.latitude, longitude: chosen.longitude, timezone: timezone || chosen.timezone || null });
        toast.success('Job site pinned');
      } else {
        await update.mutateAsync({ address: text, timezone: timezone || null });
        toast.success('Job site location saved');
      }
      setResponse(null);
      setChosen(null);
      setSearchedFor('');
    } catch (e) {
      setErr(errorMessage(e));
    }
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void find();
    }
  };

  const stale = !!response && searchedFor !== address.trim();
  const providerInfo = response?.provider;
  const savedLinks = hasCoords ? mapLinks(job.latitude!, job.longitude!) : null;

  return (
    <section id="location" className="bg-card border p-6">
      <SectionTitle className="mb-2">Job site location</SectionTitle>
      <p className="text-muted-foreground mb-5 text-sm">Powers weather triggers, local-time schedules and quiet hours for this job. Look the address up, pick the right match, and check it on a map before you save.</p>
      <div className="grid gap-4">
        <FormError message={err} />

        {/* Where the job is pinned today */}
        <div className={cn('flex flex-wrap items-center gap-x-4 gap-y-2 border-l-2 px-3 py-2 text-sm', hasCoords ? 'border-primary bg-muted/30' : 'border-warning bg-warning/10')}>
          {hasCoords ? <LocateFixedIcon className="text-primary size-4 shrink-0" aria-hidden /> : <TriangleAlertIcon className="text-warning size-4 shrink-0" aria-hidden />}
          <span className="min-w-0 flex-1">
            {hasCoords ? (
              <>
                <span className="font-medium">Pinned</span> <span className="text-muted-foreground tabular-nums">{job.latitude!.toFixed(5)}, {job.longitude!.toFixed(5)}</span>
                {job.timezone ? <span className="text-muted-foreground"> · {job.timezone} ({tzAbbreviation(job.timezone)})</span> : null}
              </>
            ) : (
              <>
                <span className="font-medium">Not pinned yet.</span> <span className="text-muted-foreground">Weather rules stay off until the site has coordinates.</span>
              </>
            )}
          </span>
          {savedLinks && (
            <span className="flex basis-full gap-1 sm:basis-auto sm:shrink-0">
              <Button asChild variant="ghost" size="xs">
                <a href={savedLinks.google} target="_blank" rel="noreferrer">
                  Google Maps <ExternalLinkIcon aria-hidden />
                </a>
              </Button>
              <Button asChild variant="ghost" size="xs">
                <a href={savedLinks.osm} target="_blank" rel="noreferrer">
                  OpenStreetMap <ExternalLinkIcon aria-hidden />
                </a>
              </Button>
            </span>
          )}
        </div>

        <Field label="Address" htmlFor="loc-address" required description="Street, city and state find the exact building. You can also paste coordinates such as 28.5405, -81.3856.">
          <div className="flex gap-2">
            <Input id="loc-address" value={address} onChange={(e) => setAddress(e.target.value)} onKeyDown={onKey} placeholder="400 W Church St, Orlando, FL 32801" disabled={!canEdit} autoComplete="street-address" />
            <Button type="button" variant={chosen ? 'outline' : 'default'} onClick={() => void find()} disabled={!canEdit || !address.trim() || looking}>
              {coordsTyped ? <LocateFixedIcon /> : <SearchIcon />} {looking ? 'Looking…' : coordsTyped ? 'Pin here' : 'Find'}
            </Button>
          </div>
        </Field>

        {response && (
          <div className="grid gap-2" aria-live="polite">
            {response.results.length === 0 ? (
              <div className="text-muted-foreground flex items-start gap-2 border border-dashed p-3 text-sm">
                <MapPinIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
                <span>
                  No match for “{searchedFor}”. Add the city and state, drop unit or suite numbers, or paste coordinates from a map.
                </span>
              </div>
            ) : (
              <>
                <div className="text-muted-foreground flex flex-wrap items-center justify-between gap-2 text-xs">
                  <span>
                    {response.results.length === 1 ? 'One match' : `${response.results.length} matches`} for “{searchedFor}”{stale ? ' (the address changed since; find again)' : ''} · via {providerLabel(providerInfo?.name)}
                  </span>
                  {chosen && <span className="text-foreground font-medium">Ready to pin</span>}
                </div>
                <ul className="grid gap-1.5" role="radiogroup" aria-label="Matches">
                  {response.results.map((r, i) => {
                    const on = chosen === r;
                    const p = PRECISION[r.precision ?? 'unknown'] ?? PRECISION['unknown']!;
                    const links = mapLinks(r.latitude, r.longitude);
                    return (
                      <li key={`${r.placeId ?? i}-${r.latitude}`} className={cn('flex flex-wrap items-start gap-3 border p-3 text-sm transition-colors', on ? 'border-primary bg-accent/40' : 'hover:border-foreground/30')}>
                        <button type="button" role="radio" aria-checked={on} onClick={() => choose(r)} className="flex min-w-0 flex-1 items-start gap-3 text-left" disabled={!canEdit}>
                          <span className={cn('mt-0.5 grid size-5 shrink-0 place-items-center border', on ? 'border-primary bg-primary text-primary-foreground' : 'bg-card text-muted-foreground')}>{on ? <CheckIcon className="size-3" aria-hidden /> : <MapPinIcon className="size-3" aria-hidden />}</span>
                          <span className="min-w-0">
                            <span className="block font-medium">{r.name && r.name !== r.street && !(r.street ?? '').endsWith(r.name) ? `${r.name} · ` : ''}{r.street ?? compactAddress(r)}</span>
                            <span className="text-muted-foreground block text-xs">{r.formattedAddress}</span>
                            <span className="text-muted-foreground mt-1 flex flex-wrap items-center gap-2 text-xs">
                              <Badge variant={r.precision === 'rooftop' ? 'default' : r.precision === 'street' ? 'outline' : 'warning'} title={p.hint}>
                                {p.label}
                              </Badge>
                              <span className="tabular-nums">
                                {r.latitude.toFixed(5)}, {r.longitude.toFixed(5)}
                              </span>
                              {r.timezone ? <span>{r.timezone}</span> : null}
                            </span>
                          </span>
                        </button>
                        <span className="flex basis-full items-center justify-end gap-1 sm:basis-auto sm:shrink-0">
                          <Button asChild variant="ghost" size="xs" title="Check this spot on Google Maps">
                            <a href={links.google} target="_blank" rel="noreferrer">
                              Map <ExternalLinkIcon aria-hidden />
                            </a>
                          </Button>
                          {on && r.provider !== 'coordinates' && compactAddress(r) !== address.trim() && (
                            <Button type="button" variant="ghost" size="xs" onClick={() => setAddress(compactAddress(r))} title="Replace what you typed with the service's version of this address">
                              Use this address
                            </Button>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <p className="text-muted-foreground text-[11px]">
                  {providerInfo?.name === 'mock' ? 'Simulated lookups (development) match a handful of US cities.' : 'Map data © OpenStreetMap contributors.'}
                  {chosen?.provider !== 'coordinates' && chosen?.timezone ? ' The timezone is estimated from the coordinates; adjust it below if the site sits near a zone boundary.' : ''}
                </p>
              </>
            )}
          </div>
        )}

        <Field label="Timezone" htmlFor="loc-tz" description={timezone ? `${tzAbbreviation(timezone)} now. Used for schedules and quiet hours.` : 'Filled in when you pick a match; edit if needed (IANA name).'}>
          <Input id="loc-tz" list="loc-tz-list" value={timezone} onChange={(e) => setTimezone(e.target.value)} placeholder="America/New_York" disabled={!canEdit} autoComplete="off" />
          {zones.length > 0 && (
            <datalist id="loc-tz-list">
              {zones.map((z) => (
                <option key={z} value={z} />
              ))}
            </datalist>
          )}
        </Field>

        {canEdit && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
            <span className="text-muted-foreground text-xs">
              {chosen ? (
                <>
                  Saving pins the job at <span className="tabular-nums">{chosen.latitude.toFixed(5)}, {chosen.longitude.toFixed(5)}</span>.
                </>
              ) : dirty && address.trim() ? (
                'Saving without picking a match uses the best guess for the address.'
              ) : hasCoords ? (
                'Change the address and find it again to move the pin.'
              ) : (
                'Find the address, then pick the right match.'
              )}
            </span>
            <span className="flex gap-2">
              {job.address && (
                <Button type="button" variant="ghost" onClick={() => setAddress('')} disabled={update.isPending || !address}>
                  <XIcon /> Clear
                </Button>
              )}
              <Button type="button" onClick={() => void save()} disabled={!dirty || update.isPending || looking}>
                {update.isPending ? 'Saving…' : !address.trim() && job.address ? 'Clear location' : chosen ? 'Pin job site' : 'Save location'}
              </Button>
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

